/**
 * ErrorBoundary Component
 * Catches JavaScript errors in child components and displays a fallback UI
 */

import { Component } from 'react';
import { theme } from '../constants/theme.js';

/**
 * Error Boundary Class Component
 * React error boundaries must be class components
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // In production, you would send this to an error tracking service
    // e.g., Sentry, LogRocket, etc.
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div
          className="min-h-[200px] flex items-center justify-center p-6"
          style={{ backgroundColor: theme.bg }}
          role="alert"
          aria-live="assertive"
        >
          <div
            className="max-w-md w-full p-6 rounded-2xl text-center"
            style={{
              backgroundColor: theme.card,
              border: `1px solid ${theme.border}`,
            }}
          >
            <div
              className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center text-3xl"
              style={{ backgroundColor: theme.errorBg }}
              aria-hidden="true"
            >
              &#9888;
            </div>
            <h2
              className="text-lg font-bold mb-2"
              style={{ color: theme.t1 }}
            >
              Something went wrong
            </h2>
            <p
              className="text-sm mb-4"
              style={{ color: theme.t4 }}
            >
              {this.props.message || 'An unexpected error occurred. Please try again.'}
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:scale-105"
                style={{ background: theme.grad, boxShadow: theme.purpleShadow }}
              >
                Try Again
              </button>
              {this.props.showReload && (
                <button
                  onClick={() => window.location.reload()}
                  className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all"
                  style={{ border: `1px solid ${theme.borderLight}`, color: theme.t3 }}
                >
                  Reload Page
                </button>
              )}
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 text-left">
                <summary
                  className="cursor-pointer text-xs"
                  style={{ color: theme.t5 }}
                >
                  Error Details (Dev Only)
                </summary>
                <pre
                  className="mt-2 p-3 rounded-lg text-xs overflow-auto"
                  style={{
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    color: theme.error,
                    maxHeight: 200,
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Inline Error Display Component
 * For displaying errors within a component without replacing it
 */
export function ErrorMessage({ error, onRetry, className = '' }) {
  if (!error) return null;

  return (
    <div
      className={`p-4 rounded-xl ${className}`}
      style={{
        backgroundColor: theme.errorBg,
        border: `1px solid rgba(239,68,68,0.2)`,
      }}
      role="alert"
    >
      <div className="flex items-center gap-3">
        <span
          className="text-lg"
          style={{ color: theme.error }}
          aria-hidden="true"
        >
          &#9888;
        </span>
        <p className="flex-1 text-sm" style={{ color: theme.error }}>
          {error}
        </p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
            style={{
              backgroundColor: 'rgba(239,68,68,0.2)',
              color: theme.error,
            }}
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Loading Skeleton Component
 * Shows a pulsing placeholder while content loads
 */
export function LoadingSkeleton({ width = '100%', height = 20, className = '' }) {
  return (
    <div
      className={`animate-pulse rounded ${className}`}
      style={{
        width,
        height,
        backgroundColor: theme.card,
      }}
      role="status"
      aria-label="Loading..."
    />
  );
}

/**
 * Loading Spinner Component
 */
export function LoadingSpinner({ size = 24, className = '' }) {
  return (
    <svg
      className={`animate-spin ${className}`}
      style={{ width: size, height: size, color: theme.teal }}
      fill="none"
      viewBox="0 0 24 24"
      role="status"
      aria-label="Loading..."
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

export default ErrorBoundary;
